import { Link, useRouterState } from "@tanstack/react-router";

import { useCapabilities } from "../session/capabilities";
import { visibleRailItems, type VisibleRailItem } from "./railItems";

function useRouteParams(): { tenant: string; scope: string } {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const m = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)/);
  return { tenant: m?.[1] ?? "", scope: m?.[2] ?? "" };
}

type ScopedTo =
  | "/t/$tenant/s/$scope/plants"
  | "/t/$tenant/s/$scope/kpi"
  | "/t/$tenant/s/$scope/actions"
  | "/t/$tenant/s/$scope/alerts"
  | "/t/$tenant/s/$scope/ledgers"
  | "/t/$tenant/s/$scope/work"
  | "/t/$tenant/s/$scope/admin"
  | "/t/$tenant/s/$scope/docs"
  | "/t/$tenant/s/$scope/reports"
  | "/t/$tenant/s/$scope/ask";

export function Rail({ open = false, onNavigate }: { open?: boolean; onNavigate?: () => void }) {
  const caps = useCapabilities();
  const items = visibleRailItems(caps);
  const { tenant: urlTenant, scope: urlScope } = useRouteParams();
  const tenant = urlTenant || caps.scopes[0]?.id || "";
  const scope = urlScope || caps.scopes[0]?.id || "";

  return (
    <nav
      aria-label="Primary"
      data-open={open ? "true" : "false"}
      style={railStyle}
      className="sk-rail"
    >
      {/* Brand mark: the SolarKey icon shipped by the stylesheet (--sk-brand-mark),
          used as-is — never redrawn or recoloured (UX-007 v1.1 §10; AMD-002 §8 item 9). */}
      <div
        role="img"
        aria-label="SolarKey"
        style={{
          width: 24,
          height: 27,
          margin: "8px 0 15px",
          flexShrink: 0,
          background: "var(--sk-brand-mark) center / contain no-repeat",
        }}
      />
      {items.map((item) => (
        <RailLink key={item.family} item={item} tenant={tenant} scope={scope} onNavigate={onNavigate} />
      ))}
    </nav>
  );
}

function RailLink({
  item, tenant, scope, onNavigate,
}: { item: VisibleRailItem; tenant: string; scope: string; onNavigate?: () => void }) {
  const routerState = useRouterState({ select: (s) => s.location.pathname });

  const resolvedPath = item.scoped
    ? `/t/${tenant}/s/${scope}/${item.to.split("/").pop()}`
    : item.to;
  const isActive = item.scoped
    ? routerState.startsWith(resolvedPath)
    : item.to === "/" && (routerState === "/" || routerState === "");

  // UX-REV-001 U5: a TIER-LOCKED item keeps a lock + upgrade cue (it reads as
  // "you can unlock this"), not a greyed-out "broken" button. In the narrow rail
  // the full LockedBadge rendered "🔒 Easy T" at meta size, which WRAPPED onto a
  // second line and looked cluttered. Keep the tier visible but compact: the
  // label, then a small no-wrap "🔒 Easy T" line that fits on one row. The full
  // upgrade hint stays in the tooltip. (LockedBadge is unchanged for wider spots.)
  if (item.locked) {
    const tier = item.locked_tier ?? "locked";
    const hint = `${item.label} is available on ${tier}. Contact your account team to enable it.`;
    return (
      <div
        style={{ ...icoStyle, cursor: "default" }}
        className="sk-rail-ico sk-rail-ico--locked"
        title={hint}
        role="note"
        aria-label={hint}
      >
        <span style={labelStyle}>{item.label}</span>
        <span style={lockedTierStyle}>
          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.5" aria-hidden="true">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {tier}
        </span>
      </div>
    );
  }

  // UX-REV-001 U5: an UNBUILT item is not shown at all — it returns when it exists.
  if (item.stub) {
    return null;
  }

  const activeStyle = isActive ? icoActiveStyle : icoStyle;

  if (!item.scoped) {
    return (
      <Link to="/" onClick={onNavigate} style={activeStyle} className="sk-rail-ico" aria-current={isActive ? "page" : undefined}>
        <span style={labelStyle}>{item.label}</span>
      </Link>
    );
  }

  return (
    <Link
      to={item.to as ScopedTo}
      params={{ tenant, scope }}
      onClick={onNavigate}
      style={activeStyle}
      className="sk-rail-ico"
      aria-current={isActive ? "page" : undefined}
    >
      <span style={labelStyle}>{item.label}</span>
    </Link>
  );
}

const railStyle: React.CSSProperties = {
  width: 56,
  background: "var(--sk-color-surface-0)",
  borderRight: "1px solid var(--sk-color-border-1)",
  padding: "12px 0",
  display: "flex",
  flexDirection: "column",
  gap: 2,
  alignItems: "center",
  flexShrink: 0,
  overflowY: "auto",
  overflowX: "hidden",
};

const icoStyle: React.CSSProperties = {
  width: 44,
  minHeight: 38,
  borderRadius: 8,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 1,
  fontSize: 9,
  // Rail labels read in the strong ink so every item is clearly legible, not a
  // faint grey (team request: consistent, readable rail). Locked items inherit
  // this too, so they match rather than reading accidentally darker/lighter.
  color: "var(--sk-color-ink-1)",
  letterSpacing: "0.02em",
  background: "transparent",
  border: 0,
  cursor: "pointer",
  textDecoration: "none",
  padding: "4px 2px",
  lineHeight: 1.2,
};

const icoActiveStyle: React.CSSProperties = {
  ...icoStyle,
  background: "var(--sk-color-accent-soft)",
  color: "var(--sk-color-accent)",
  fontWeight: 600,
};

const labelStyle: React.CSSProperties = {
  fontSize: 9,
  lineHeight: 1.2,
  textAlign: "center",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: 50,
};

// The tier cue under a locked label: a small lock + tier (e.g. "Easy T") kept on
// ONE line at the rail's width, so it reads as the cue it is without wrapping.
const lockedTierStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  fontSize: 8.5,
  lineHeight: 1,
  whiteSpace: "nowrap",
  // Slightly lighter than the label so the "Easy T" cue reads as secondary.
  color: "var(--sk-color-ink-2)",
  letterSpacing: "0.02em",
};

